/**
 * Viko Call on the web and desktop (VC8): one call at a time, app-wide. Starts the call agent on
 * your Mac, listens on the call's sealed frame stream, hears you locally (on-device recognition
 * or your Mac's whisper, never a cloud recognizer), speaks with local voices, runs the fast path
 * on the device, signs voice-tier actions and holds the passkey batch.
 *
 * Authority: the device signs. Voice-tier actions are normal device-signed commands carrying the
 * call's id ("via call"); landing, production and risky decisions only run from the secure batch
 * after an explicit approve (a click, ⌘↵, or a spoken "approve" while this tab is visible and
 * focused), one passkey for the batch.
 */
import {
  type CallFrame,
  type CallInterruption,
  type CallSecureItem,
  type CallShowTarget,
  type CommandKind,
  type CommandPayload,
  isProductionLane,
  type TranscriptEvent,
} from '@viko/protocol'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import { useDecisionActions } from '../../components/decision/DecisionPanel.js'
import { useNeedsYou } from '../../components/inbox/useNeedsYou.js'
import { useToast } from '../../components/ui.js'
import { useBackend } from '../backend.js'
import { viewFromDecision } from '../decision-view.js'
import { decisionRoute } from '../decisions.js'
import { useMachines } from '../queries.js'
import { isTyping } from '../shortcut.js'
import type { TrayRequest } from '../signer.js'
import { speechPathFor } from '../speech.js'
import { type DevicePlan, nextItemSpeech, pickedText, planFor } from './device-actions.js'
import {
  type LocalRecognition,
  localRecognitionClass,
  speechLang,
  startLocalRecognition,
} from './local-speech.js'
import { anyCallMac, type CallMac, pickCallMac } from './mac.js'
import { CallMacContext } from './mac-context.js'
import { EnergyVad, type PcmRecorder, recordPcm16, transcribeOnMac } from './mac-whisper.js'
import { callBlockerFrom } from './problems.js'
import {
  CallSpeaker,
  isEcho,
  pickLocalVoice,
  type UtteranceLike,
  type VoiceLike,
} from './speaker.js'
import { type CallState, callReducer, callSummary, currentItem, initialCall } from './state.js'

export interface CallApi {
  state: CallState
  /** Your Mac (where the call agent runs), if one is online. */
  mac: CallMac | undefined
  /** The Mac a call would use, online or offline (for its live status in the panel). */
  macStatus: CallMac | undefined
  needs: number
  /**
   * Start a call. Locked content is unlocked first, in the same tap (the one passkey ceremony);
   * anything else that stops the call shows as a `blocked` state with one action.
   */
  start(o?: { focus?: string; openMic?: boolean }): Promise<void>
  end(): Promise<void>
  /** Leave the call view; the call keeps running behind the "On call" pill. */
  minimise(): void
  /** Back to the call view from the pill. */
  restore(): void
  /** Close an ended (or never started) call's view. */
  dismiss(): void
  /** Typed or clicked input, handled exactly like speech. */
  say(text: string): Promise<void>
  pick(n: number, note?: string): Promise<void>
  /** A note alone as the answer. */
  note(text: string): Promise<void>
  skip(): Promise<void>
  approveSecure(): Promise<void>
  removeSecure(id: string): void
  closeShow(): void
  show(target: CallShowTarget): void
  pttDown(): void
  pttUp(): void
  toggleMute(): void
  stopSpeaking(): void
}

const CallContext = createContext<CallApi | null>(null)

export function useCall(): CallApi {
  const c = useContext(CallContext)
  if (!c) throw new Error('useCall outside CallProvider')
  return c
}
/** For optional surfaces (tests render pieces without the provider). */
export function useMaybeCall(): CallApi | null {
  return useContext(CallContext)
}

const uid = () => crypto.randomUUID()
/** How long a call that changed nothing shows "Call ended" before closing by itself. */
export const QUIET_CLOSE_MS = 1600

function formatDuration(ms: number): string {
  const sec = Math.max(0, Math.round(ms / 1000))
  return `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`
}
const short = () => Math.random().toString(36).slice(2, 10)

function browserSpeaker(onSpeaking: (on: boolean) => void): CallSpeaker | undefined {
  const synth = (globalThis as { speechSynthesis?: SpeechSynthesis }).speechSynthesis
  const U = (globalThis as { SpeechSynthesisUtterance?: typeof SpeechSynthesisUtterance })
    .SpeechSynthesisUtterance
  if (!synth || !U) return undefined
  const lang = speechLang()
  return new CallSpeaker({
    synth: synth as unknown as { speak(u: UtteranceLike): void; cancel(): void },
    make: (text) => new U(text) as unknown as UtteranceLike,
    // Local system voices only: a remote ("Google …") voice is never picked.
    voice: () => pickLocalVoice(synth.getVoices() as unknown as VoiceLike[], lang),
    lang,
    onSpeaking,
  })
}

export function CallProvider({ children }: { children: ReactNode }) {
  const b = useBackend()
  const toast = useToast()
  const needs = useNeedsYou()
  const machines = useMachines()
  const mac = pickCallMac(machines.data)
  const macStatus = anyCallMac(machines.data)
  const decisionActions = useDecisionActions()
  const [state, dispatch] = useReducer(callReducer, initialCall)
  const st = useRef(state)
  st.current = state

  const r = useRef<{
    unsub?: () => void
    rec?: LocalRecognition
    recorder?: PcmRecorder
    vad?: EnergyVad
    segment?: Int16Array[]
    speaker?: CallSpeaker
    cut?: CallInterruption
    acted: { id: string; kind: string; title: string; projectId?: string }[]
    handledActs: Set<string>
    openMic: boolean
    pttAt?: number
    lastSpoken: string
  }>({ acted: [], handledActs: new Set(), openMic: false, lastSpoken: '' })

  const speaker = useCallback(() => {
    r.current.speaker ??= browserSpeaker((on) => dispatch({ type: 'SPEAKING', on }))
    return r.current.speaker
  }, [])

  /** Speak on the device from stored data (fast path), captioned as Viko. */
  const localSay = useCallback(
    (text: string) => {
      dispatch({ type: 'LOCAL_SAY', id: `l:${short()}`, text })
      r.current.lastSpoken = text
      speaker()?.say(`local-${short()}`, text)
    },
    [speaker],
  )

  const send = useCallback(
    async <K extends CommandKind>(kind: K, payload: CommandPayload<K>, machineId?: string) => {
      const m = machineId ?? st.current.machine?.id
      if (!m) throw new Error('No Mac for this call.')
      return b.dispatch(kind, m, payload)
    },
    [b],
  )

  /** Tell the agent something the device handled (no turn of its own; rides on the next one). */
  const handled = useCallback(
    async (text: string, note: string) => {
      const s = st.current
      if (!s.callId) return
      const acted = r.current.acted.splice(0, 10)
      await send('call.utterance', {
        callId: s.callId,
        utteranceId: `u${short()}`,
        text,
        handled: note.slice(0, 400),
        ...(acted.length ? { acted } : {}),
      }).catch(() => {})
    },
    [send],
  )

  // ---- device actions ------------------------------------------------------------------------

  const answerDecision = useCallback(
    async (decisionId: string, optionId: string | undefined, note: string | undefined) => {
      const s = st.current
      const d = decisionActions.store
        ? (await decisionActions.store.list()).find((x) => x.id === decisionId)
        : undefined
      if (!d || !decisionActions.store) throw new Error('That decision is not on this device yet.')
      const route =
        decisionActions.routeFor(viewFromDecision(d, Date.now())) ??
        decisionRoute(d, machines.data ?? [], undefined)
      if (!route) throw new Error('No machine can take this answer right now.')
      const view = viewFromDecision(d, Date.now())
      const out = await decisionActions.store.answer(
        view,
        optionId
          ? { mode: 'pick', optionId, ...(note ? { note } : {}) }
          : { mode: 'freeform', text: note ?? '' },
        route,
        { callId: s.callId },
      )
      // Answered on another machine: the call's Mac records it from the next utterance.
      if (route.machineId !== s.machine?.id && out.commandId)
        r.current.acted.push({
          id: out.commandId,
          kind: 'decision.answer',
          title: optionId ? `Answered: ${view.title}` : `Noted: ${view.title}`,
          projectId: d.projectId,
        })
    },
    [decisionActions, machines.data],
  )

  const run = useCallback(
    async (plan: DevicePlan, said: string) => {
      const s = st.current
      switch (plan.kind) {
        case 'pick': {
          dispatch({ type: 'MARK', key: plan.item.key, as: 'answered' })
          const next = nextItemSpeech(s, plan.item.key)
          localSay(`${pickedText(plan.option, plan.note)} ${next.text}`)
          try {
            await answerDecision(plan.item.decision!.id, plan.option.id, plan.note)
          } catch (e) {
            localSay(`That didn't go through: ${e instanceof Error ? e.message : 'unknown error'}`)
          }
          return handled(
            said,
            `Picked ${plan.option.n} "${plan.option.label}" for ${plan.item.projectName}: ${plan.item.title}${plan.note ? `, note "${plan.note}"` : ''}. Read the next item aloud already.`,
          )
        }
        case 'skip': {
          dispatch({ type: 'MARK', key: plan.item.key, as: 'skipped' })
          localSay(nextItemSpeech(s, plan.item.key).text)
          return handled(said, `Skipped ${plan.item.projectName}: ${plan.item.title}.`)
        }
        case 'repeat':
          if (r.current.lastSpoken) localSay(r.current.lastSpoken)
          return
        case 'stop':
          speaker()?.stop()
          return
        case 'approve':
          // A spoken approve opens the passkey prompt only while you're looking at this tab.
          if (document.visibilityState === 'visible' && document.hasFocus())
            return void approveRef.current()
          localSay('Your passkey batch is waiting on screen. Approve it there.')
          return
        case 'status':
          localSay(plan.answer)
          return handled(said, `Answered from the brief: "${plan.answer.slice(0, 300)}"`)
        case 'agent':
          return
      }
    },
    [answerDecision, handled, localSay, speaker],
  )

  /** Something the user said (speech, PTT or typed). */
  const heard = useCallback(
    async (text: string) => {
      const s = st.current
      const t = text.trim()
      if (!t || !s.callId) return
      // Echo guard: the mic heard our own voice.
      if (speaker()?.speaking && isEcho(t, speaker()?.recentText() ?? '')) return
      dispatch({ type: 'HEARD', id: `h:${short()}`, text: t })
      const plan = planFor(s, t)
      if (plan.kind !== 'agent') return run(plan, t)
      const cut = r.current.cut ? (speaker()?.takeCut(r.current.cut) ?? r.current.cut) : undefined
      r.current.cut = undefined
      const acted = r.current.acted.splice(0, 10)
      const cur = currentItem(s)
      try {
        await send('call.utterance', {
          callId: s.callId,
          utteranceId: `u${short()}`,
          text: t.slice(0, 4000),
          ...(cut ? { interrupted: cut } : {}),
          ...(cur ? { current: cur.key } : {}),
          ...(acted.length ? { acted } : {}),
        })
      } catch (e) {
        dispatch({
          type: 'ERROR',
          message: e instanceof Error ? e.message : 'Viko did not get that.',
        })
      }
    },
    [run, send, speaker],
  )
  const heardRef = useRef(heard)
  heardRef.current = heard

  /** Barge-in: stop talking now and remember exactly where. */
  const bargeIn = useCallback(() => {
    const sp = speaker()
    if (!sp?.speaking) return
    const cut = sp.cut()
    if (cut) {
      r.current.cut = cut
      dispatch({ type: 'CUT', cut })
    }
  }, [speaker])

  // ---- listening -----------------------------------------------------------------------------

  const stopListening = useCallback(() => {
    r.current.rec?.abort()
    r.current.rec = undefined
    r.current.recorder?.cancel()
    r.current.recorder = undefined
    r.current.vad = undefined
    dispatch({ type: 'LISTEN', on: false })
  }, [])

  const transcribe = useCallback(
    async (pcm: Int16Array) => {
      const s = st.current
      if (!s.callId || !s.machine || pcm.length < 1600) return
      dispatch({ type: 'INTERIM', text: 'Transcribing on your Mac…' })
      try {
        const text = await transcribeOnMac(pcm, {
          callId: s.callId,
          utteranceId: `a${short()}`,
          send: async (payload) =>
            (await b.dispatch('call.audio', s.machine!.id, payload)).result as { text?: string },
        })
        dispatch({ type: 'INTERIM', text: '' })
        if (text) await heardRef.current(text)
      } catch (e) {
        dispatch({ type: 'INTERIM', text: '' })
        dispatch({
          type: 'ERROR',
          message: `Transcription failed: ${e instanceof Error ? e.message : 'unknown error'}`,
        })
      }
    },
    [b],
  )

  const listen = useCallback(
    async (mode: 'open' | 'ptt') => {
      const s = st.current
      if (!s.callId || s.muted) return
      stopListening()
      const path = s.speech?.path
      if (path === 'browser-local') {
        const C = localRecognitionClass()
        if (!C) return
        try {
          r.current.rec = startLocalRecognition(C, speechLang(), {
            continuous: mode === 'open',
            onSpeechStart: bargeIn,
            onInterim: (text) => {
              if (text) bargeIn()
              dispatch({ type: 'INTERIM', text })
            },
            onFinal: (text) => void heardRef.current(text),
            onError: (e) => dispatch({ type: 'ERROR', message: e.message }),
            onEnd: () => {
              r.current.rec = undefined
              dispatch({ type: 'LISTEN', on: false })
              // Open mic keeps listening across recognizer restarts (still on-device only).
              if (
                r.current.openMic &&
                !st.current.muted &&
                st.current.phase === 'live' &&
                !st.current.ptt
              )
                setTimeout(() => void listenRef.current('open'), 250)
            },
          })
          dispatch({ type: 'LISTEN', on: true })
        } catch (e) {
          dispatch({
            type: 'ERROR',
            message: e instanceof Error ? e.message : 'On-device speech failed.',
          })
        }
        return
      }
      if (path === 'mac-whisper') {
        try {
          const vad = mode === 'open' ? new EnergyVad() : undefined
          r.current.vad = vad
          r.current.segment = []
          r.current.recorder = await recordPcm16({
            onFrame: (f) => {
              if (!vad) return
              const was = vad.speaking
              const t = vad.push(f)
              if (t === 'start') bargeIn()
              if (was || vad.speaking) r.current.segment?.push(f)
              if (t === 'end') {
                const seg = r.current.segment ?? []
                r.current.segment = []
                const total = seg.reduce((n, x) => n + x.length, 0)
                const pcm = new Int16Array(total)
                let at = 0
                for (const x of seg) {
                  pcm.set(x, at)
                  at += x.length
                }
                void transcribe(pcm)
              }
            },
            onAutoStop: () => {
              if (mode === 'ptt') pttUpRef.current()
            },
          })
          dispatch({ type: 'LISTEN', on: true })
        } catch {
          dispatch({ type: 'ERROR', message: 'Microphone access was denied.' })
        }
      }
    },
    [bargeIn, stopListening, transcribe],
  )
  const listenRef = useRef(listen)
  listenRef.current = listen

  // ---- frames --------------------------------------------------------------------------------

  const onAct = useCallback(
    async (f: Extract<CallFrame, { t: 'act' }>) => {
      if (r.current.handledActs.has(f.id)) return
      r.current.handledActs.add(f.id)
      const s = st.current
      const a = f.action
      try {
        if (a.kind === 'decision.pick') {
          const item = s.queue.find((q) => q.key === `d:${a.decisionId}`)
          const opt = item?.decision?.options.find((o) => o.n === a.n)
          // Validated against the stored record: never the agent's wording, never a risky one.
          if (!item?.decision || !opt || item.decision.secure)
            throw new Error('not a voice-tier pick')
          dispatch({ type: 'MARK', key: item.key, as: 'answered' })
          await answerDecision(a.decisionId, opt.id, a.note)
        } else if (a.kind === 'decision.note') {
          await answerDecision(a.decisionId, undefined, a.note)
        } else if (a.kind === 'run.message') {
          await send('run.message', { runId: a.runId, text: a.text, callId: s.callId })
        } else if (a.kind === 'run.stop') {
          await send('run.stop', { runId: a.runId, callId: s.callId! })
        } else if (a.kind === 'ship.preview') {
          if (isProductionLane(a.lane)) throw new Error('production needs the passkey')
          await send('call.ship', { callId: s.callId!, projectId: a.projectId, lane: a.lane })
        }
      } catch (e) {
        await handled('', `Could not sign ${a.kind}: ${e instanceof Error ? e.message : 'failed'}.`)
      }
    },
    [answerDecision, handled, send],
  )

  const onFrame = useCallback(
    (f: CallFrame) => {
      dispatch({ type: 'FRAME', frame: f })
      if (f.t === 'say') {
        const sp = speaker()
        sp?.turn(f.turnId)
        if (f.text && !st.current.ptt) {
          r.current.lastSpoken = f.text
          sp?.say(f.turnId, f.text)
        }
      } else if (f.t === 'notify') localSay(f.text)
      else if (f.t === 'act') void onAct(f)
      else if (f.t === 'state' && f.state === 'ended') {
        stopListening()
        speaker()?.stop()
      } else if (f.t === 'secure') {
        // Production ships are offered at once: say so (the passkey still needs an explicit approve).
        const fresh = f.items.filter(
          (x) =>
            x.production && x.state === 'queued' && !st.current.secure.some((y) => y.id === x.id),
        )
        if (fresh.length) dispatch({ type: 'SHOW', target: { type: 'secure-batch' } })
      }
    },
    [localSay, onAct, speaker, stopListening],
  )

  // ---- lifecycle -----------------------------------------------------------------------------

  const start = useCallback(
    async (o: { focus?: string; openMic?: boolean } = {}) => {
      const cur = st.current
      if (cur.phase === 'starting' || cur.phase === 'live') return
      // Locked here: the Start tap IS the unlock tap (one passkey ceremony), then the call.
      const lock = b.lock
      const locked = () => {
        const x = lock?.get().status
        return x === 'locked' || x === 'unlocking'
      }
      if (lock && locked()) {
        dispatch({ type: 'BLOCKED', blocker: { kind: 'locked' } })
        const r = await lock.unlock().catch(() => undefined)
        if (r?.status !== 'unlocked' && locked()) return
      }
      if (!mac) {
        dispatch({ type: 'BLOCKED', blocker: { kind: 'offline' } })
        return
      }
      const callId = uid()
      const speech = await speechPathFor(mac)
      dispatch({ type: 'START', callId, machine: mac, speech, at: Date.now() })
      r.current.openMic = o.openMic ?? speech.path === 'browser-local'
      r.current.handledActs.clear()
      const runId = `call-${callId}`
      // Subscribe first: the stream replays from the start, so no frame is missed.
      r.current.unsub?.()
      r.current.unsub = b.hub.subscribe(runId, 0, {
        onEvents: (events: TranscriptEvent[]) => {
          for (const e of events) if (e.kind === 'call.frame') onFrame(e.frame)
        },
      })
      try {
        await b.dispatch('call.start', mac.id, {
          callId,
          mode: 'local',
          speech: speech.path === 'none' ? 'typed' : speech.path,
          ...(o.focus ? { focus: o.focus } : {}),
        })
        dispatch({ type: 'STARTED', runId })
        if (r.current.openMic) void listen('open')
      } catch (e) {
        r.current.unsub?.()
        r.current.unsub = undefined
        // Never a raw Error in the panel: a known kind with human copy (details behind a toggle).
        const blocker = callBlockerFrom(e)
        if (blocker.kind === 'locked') void lock?.check()
        dispatch({ type: 'BLOCKED', blocker })
      }
    },
    [b, listen, mac, onFrame],
  )

  const end = useCallback(async () => {
    const s = st.current
    stopListening()
    speaker()?.stop()
    r.current.openMic = false
    if (s.phase === 'blocked' || s.phase === 'error' || s.phase === 'idle') {
      dispatch({ type: 'RESET' })
      return
    }
    if (s.callId && s.machine && s.phase !== 'ended') {
      await send('call.end', { callId: s.callId }).catch(() => {})
    }
    dispatch({ type: 'ENDED', at: Date.now() })
    // Keep the stream a moment for the final frames, then let go.
    setTimeout(() => {
      r.current.unsub?.()
      r.current.unsub = undefined
    }, 2000)
  }, [send, speaker, stopListening])

  // A call that changed nothing closes by itself (with a toast); otherwise the summary stays.
  const endedQuietly = state.phase === 'ended' && callSummary(state).quiet
  useEffect(() => {
    if (!endedQuietly) return
    const t = setTimeout(() => {
      const s = st.current
      if (s.phase !== 'ended') return
      toast(`Call ended · ${formatDuration(callSummary(s).durationMs)} · nothing changed`)
      dispatch({ type: 'RESET' })
    }, QUIET_CLOSE_MS)
    return () => clearTimeout(t)
  }, [endedQuietly, toast])

  const approveSecure = useCallback(async () => {
    const s = st.current
    const items = s.secure.filter((x) => x.state === 'queued')
    if (!items.length || !s.machine) return
    const decisions = decisionActions.store ? await decisionActions.store.list() : []
    const reqs: TrayRequest[] = items.map((x) => {
      let machineId = s.machine!.id
      if (x.kind === 'decision.answer.risky') {
        const d = decisions.find((y) => y.id === x.payload.decisionId)
        const route = d ? decisionRoute(d, machines.data ?? [], undefined) : undefined
        if (route) machineId = route.machineId
      }
      return {
        kind: x.kind,
        machineId,
        payload: x.payload,
        opts: { id: x.id, summary: x.title, production: x.production },
        summary: x.title,
        production: x.production,
      }
    })
    try {
      const signed = await b.signer.signTapped(reqs)
      dispatch({ type: 'SECURE_STATE', ids: items.map((x) => x.id), state: 'approved' })
      const outs = await Promise.all(signed.map((c) => b.deliver(c)))
      const failed = items.filter(
        (_, i) => outs[i]?.status === 'failed' || outs[i]?.status === 'rejected',
      )
      if (failed.length)
        dispatch({ type: 'SECURE_STATE', ids: failed.map((x) => x.id), state: 'failed' })
      for (let i = 0; i < reqs.length; i++)
        if (reqs[i]!.machineId !== s.machine.id)
          r.current.acted.push({
            id: items[i]!.id,
            kind: items[i]!.kind,
            title: items[i]!.title,
            projectId: items[i]!.projectId,
          })
      localSay(
        failed.length
          ? `${items.length - failed.length} approved; ${failed.length} failed. They're on screen.`
          : `Approved ${items.length}. On their way.`,
      )
    } catch (e) {
      dispatch({
        type: 'ERROR',
        message: e instanceof Error ? e.message : 'The passkey was not approved.',
      })
    }
  }, [b, decisionActions.store, localSay, machines.data])
  const approveRef = useRef(approveSecure)
  approveRef.current = approveSecure

  const pttDown = useCallback(() => {
    const s = st.current
    if (s.phase !== 'live') return
    bargeIn()
    r.current.pttAt = Date.now()
    dispatch({ type: 'PTT', on: true })
    void listen('ptt')
  }, [bargeIn, listen])

  const pttUp = useCallback(() => {
    if (!st.current.ptt) return
    dispatch({ type: 'PTT', on: false })
    const rec = r.current.recorder
    if (rec) {
      r.current.recorder = undefined
      dispatch({ type: 'LISTEN', on: false })
      void rec.stop().then((pcm) => transcribe(pcm))
    } else r.current.rec?.stop()
    if (r.current.openMic) setTimeout(() => void listenRef.current('open'), 300)
  }, [transcribe])
  const pttUpRef = useRef(pttUp)
  pttUpRef.current = pttUp

  // ⌘⇧V anywhere (except a text field, where it pastes): hold to talk; tap opens a call with the
  // mic on, or mutes/unmutes during one.
  useEffect(() => {
    let down = 0
    const isCombo = (e: KeyboardEvent) =>
      (e.metaKey || e.ctrlKey) &&
      e.shiftKey &&
      (e.key === 'v' || e.key === 'V' || e.code === 'KeyV')
    const onDown = (e: KeyboardEvent) => {
      if (!isCombo(e) || e.repeat || isTyping(e)) return
      e.preventDefault()
      down = Date.now()
      if (st.current.phase === 'live') pttDown()
    }
    const onUp = (e: KeyboardEvent) => {
      if (
        !down ||
        !(
          e.key === 'v' ||
          e.key === 'V' ||
          e.key === 'Meta' ||
          e.key === 'Control' ||
          e.key === 'Shift'
        )
      )
        return
      const held = Date.now() - down
      down = 0
      if (st.current.ptt) {
        if (held >= 350) return pttUp()
        // A tap during a call: release PTT and toggle mute.
        dispatch({ type: 'PTT', on: false })
        stopListening()
        toggleRef.current()
        return
      }
      if (st.current.phase !== 'live' && st.current.phase !== 'starting')
        void start({ openMic: true })
    }
    window.addEventListener('keydown', onDown)
    window.addEventListener('keyup', onUp)
    return () => {
      window.removeEventListener('keydown', onDown)
      window.removeEventListener('keyup', onUp)
    }
  }, [pttDown, pttUp, start, stopListening])

  const toggleMute = useCallback(() => {
    const on = !st.current.muted
    dispatch({ type: 'MUTE', on })
    if (on) stopListening()
    else if (r.current.openMic) void listen('open')
  }, [listen, stopListening])
  const toggleRef = useRef(toggleMute)
  toggleRef.current = toggleMute

  useEffect(
    () => () => {
      r.current.unsub?.()
      r.current.rec?.abort()
      r.current.recorder?.cancel()
      r.current.speaker?.stop()
    },
    [],
  )

  // Dictation anywhere in the app uses the same Mac path (encrypted accounts only).
  const encrypted = !!b.content
  const macSpeech = useMemo(
    () =>
      mac && encrypted
        ? {
            mac,
            send: async (payload: CommandPayload<'call.audio'>) =>
              (await b.dispatch('call.audio', mac.id, payload)).result as { text?: string },
          }
        : undefined,
    [b, mac, encrypted],
  )

  const api = useMemo<CallApi>(
    () => ({
      state,
      mac,
      macStatus,
      needs: needs.total,
      start,
      end,
      minimise: () => {
        const p = st.current.phase
        if (p === 'live' || p === 'starting') dispatch({ type: 'VIEW', view: 'min' })
        else dispatch({ type: 'RESET' })
      },
      restore: () => dispatch({ type: 'VIEW', view: 'open' }),
      dismiss: () => {
        const p = st.current.phase
        if (p === 'live' || p === 'starting') return
        dispatch({ type: 'RESET' })
      },
      say: (text) => heard(text),
      pick: async (n, note) => {
        const s = st.current
        const item = currentItem(s)
        const option = item?.decision?.options.find((o) => o.n === n)
        if (!item || !option) return
        if (item.decision?.secure) {
          await heard(`${n}${note ? `, ${note}` : ''}`)
          return
        }
        dispatch({ type: 'HEARD', id: `h:${short()}`, text: `${n}${note ? `, ${note}` : ''}` })
        await run({ kind: 'pick', item, option, ...(note ? { note } : {}) }, `picked ${n}`)
      },
      note: async (text) => {
        const s = st.current
        const item = currentItem(s)
        if (!item?.decision || !text.trim()) return
        dispatch({ type: 'HEARD', id: `h:${short()}`, text })
        dispatch({ type: 'MARK', key: item.key, as: 'answered' })
        localSay(`Noted. ${nextItemSpeech(s, item.key).text}`)
        await answerDecision(item.decision.id, undefined, text.trim()).catch((e) =>
          localSay(`That didn't go through: ${e instanceof Error ? e.message : 'unknown error'}`),
        )
        await handled(
          text,
          `Answered ${item.projectName}: ${item.title} with a note: "${text.slice(0, 200)}".`,
        )
      },
      skip: async () => {
        const item = currentItem(st.current)
        if (item) await run({ kind: 'skip', item }, 'skip')
      },
      approveSecure,
      removeSecure: (id: string) => {
        dispatch({ type: 'SECURE_STATE', ids: [id], state: 'removed' })
        const x = st.current.secure.find((y) => y.id === id)
        if (x) void handled('', `The user removed "${x.title}" from the passkey batch.`)
      },
      closeShow: () => dispatch({ type: 'SHOW', target: undefined }),
      show: (target) => dispatch({ type: 'SHOW', target }),
      pttDown,
      pttUp,
      toggleMute,
      stopSpeaking: () => speaker()?.stop(),
    }),
    [
      state,
      mac,
      macStatus,
      needs.total,
      start,
      end,
      heard,
      run,
      localSay,
      answerDecision,
      handled,
      approveSecure,
      pttDown,
      pttUp,
      toggleMute,
      speaker,
    ],
  )
  return (
    <CallContext.Provider value={api}>
      <CallMacContext.Provider value={macSpeech}>{children}</CallMacContext.Provider>
    </CallContext.Provider>
  )
}

export type { CallSecureItem }
